import { useQuery } from '@tanstack/react-query';
import { endOfDay, endOfHour, sub } from 'date-fns';
import { UsersIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@logspot-platform/ui/tooltip';
import { AreaChart } from './charts/area-chart';
import { ChangeBadge } from './change-badge';
import { Widget } from './widget';
import { useWidgetsContext } from './widgets-context';
import { getDau } from './api/widgets-service';
import { numberWithCommas } from './lib/number-with-commas';
import type { DateRange } from './types';

export const DauWidget = ({ dateRange }: { dateRange: DateRange }) => {
  const { api, projectId, filterGroups } = useWidgetsContext();
  const prevDateRange = {
    interval: dateRange.interval,
    startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
    endDate: dateRange.interval.includes('days')
      ? endOfDay(dateRange.startDate)
      : endOfHour(dateRange.startDate),
  };

  const { isLoading, data } = useQuery({
    queryKey: [`dau`, projectId, dateRange, filterGroups],
    queryFn: () =>
      getDau(api, {
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        filterGroups,
      }),
  });
  const { data: prevData } = useQuery({
    queryKey: [`prev-dau`, projectId, dateRange, filterGroups],
    queryFn: () =>
      getDau(api, {
        projectId,
        interval: prevDateRange.interval,
        startDate: prevDateRange.startDate,
        endDate: prevDateRange.endDate,
        filterGroups,
      }),
  });

  const currentTotal = parseInt(data?.total ?? '0') || 0;
  const currentLive = parseInt(data?.live ?? '0') || 0;
  const prevTotal = parseInt(prevData?.total ?? '0') || 0;

  return (
    <Widget
      loading={isLoading}
      Header={() => (
        <>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xl font-medium">
              <UsersIcon className="text-primary" />
              <span className="font-semibold">Unique Users</span>
            </span>
            <span className="flex items-center gap-2">
              {!!data && (
                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-3xl leading-none font-black">
                          {numberWithCommas(currentTotal)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Unique Users</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <ChangeBadge current={currentTotal} previous={prevTotal} />
                </div>
              )}
            </span>
          </div>
        </>
      )}
      SubHeader={() => (
        <div className="ml-5 flex items-center gap-1">
          <div className="space-x-2">
            <div className="inline-block size-3 animate-pulse rounded-full bg-green-500"></div>
            <span className="text-xl font-bold">{currentLive} Online</span>
          </div>
        </div>
      )}
    >
      {!isLoading && (
        <AreaChart
          data={data?.perDay ?? []}
          dateRange={dateRange}
          prevData={prevData?.perDay}
          prevDateRange={prevDateRange}
        />
      )}
    </Widget>
  );
};
